import {
  Button,
  Col,
  Progress,
  Row,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import { useEffect, useState, useCallback } from "react";
import { ArrowUpOutlined } from '@ant-design/icons';
import { HasAccess } from '@permify/react-role';
import { getCalculatorData, calculateExamResult } from "../../../../services/exam";

const CalculatorForm = ({
  inputs,
  admissionPatientId,
  admissionPatientExamId,
  disableForm,
  roles,
  calculatorState,
  setCalculatorState,
  calculatorData,
  setCalculatorData,
  from
}) => {
  const [progressState, setProgress] = useState(0);
  const [loadingState, setLoading] = useState(false);

  const updateProgress = useCallback((data) => {
    if (!data) {
      setProgress(0);
      return;
    }

    const relevantFields = Object.values(data).filter(
      item => item && typeof item === 'object' &&
        item.hasOwnProperty('displayName') &&
        item.hasOwnProperty('value')
    );

    if (relevantFields.length === 0) {
      setProgress(0);
      return;
    }

    const completedFields = relevantFields.filter(
      item => item.value !== null && item.value !== undefined
    ).length;

    const percentage = (completedFields * 100) / relevantFields.length;
    setProgress(percentage);
  }, []);

  useEffect(() => {
    const fetchCalculatorData = async () => {
      if (!admissionPatientExamId || !inputs?.form_type || !admissionPatientId) {
        setCalculatorData(null);
        updateProgress(null);
        return;
      }
      try {
        setLoading(true);
        const response = await getCalculatorData({
          admissionPatientId,
          formType: inputs.form_type,
        });
        setCalculatorData(response);
        updateProgress(response);
      } catch (error) {
        message.error({ content: 'Error al obtener datos calculadora', style: { marginTop: '45vh' }, });
        setCalculatorData(null);
        updateProgress(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCalculatorData();
  }, [admissionPatientExamId, inputs?.form_type, admissionPatientId, updateProgress]);

  useEffect(() => {
    if (inputs?.procedures?.length > 0) {
      const initialProcedures = inputs.procedures.map((procedure, index) => ({
        id: procedure.id,
        name: procedure.name,
        result: procedure.result ?? null,
        result_type: procedure.result_type,
        order: procedure.order ?? index + 1,
      }));

      setCalculatorState(initialProcedures);
    } else {
      setCalculatorState([]);
    }
  }, [inputs, setCalculatorState]);

  const fetchCalculator = async () => {
    try {
      const response = await calculateExamResult({
        admissionPatientExamId,
        body: {
          form_type: inputs.form_type,
          procedures: inputs.procedures
            .sort((first, second) => first.order - second.order)
            .map(procedure => ({
              id: procedure.id,
              procedure_order: procedure.order
            })),
          data: {
            ...calculatorData,
          }
        },
      });
      return response;
    } catch (error) {
      message.error({ content: 'Error al calcular resultado', style: { marginTop: '45vh' }, });
    }
  }

  const handleCalculate = async () => {
    try {
      setLoading(true);
      const response = await fetchCalculator();

      setCalculatorState(prevState => {
        const updatedProcedures = prevState.map(item => {
          const resultItem = response.find(res => res.id === item.id);
          if (resultItem) {
            return { ...item, result: resultItem.result };
          }
          return item;
        });
        return updatedProcedures;
      });

      message.success({
        content: 'Cálculo completado',
        style: { marginTop: '40vh', },
      });
      setLoading(false);
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al calcular resultado',
        style: { marginTop: '40vh', },
      });
      setLoading(false);
    }
  };

  const renderDisplayTable = () => {

    const dataEntries = Object.entries(calculatorData || {})
      .filter(([key, item]) =>
        item && typeof item === 'object' &&
        item.hasOwnProperty('displayName') &&
        item.hasOwnProperty('value')
      )
      .map(([key, item]) => {
        let displayValue;
        let currentValue = item.value;
        if (typeof currentValue === 'boolean') {
          displayValue = currentValue ? 'Sí' : 'No';
        } else if (item.value !== null) {
          displayValue = String(currentValue);
        } else {
          displayValue = 'No completado';
        }

        if (key === 'gender') {
          displayValue = item.value === '0' ? 'Masculino' : 'Femenino';
        }

        if (item.unit !== null && item.unit !== undefined && String(item.unit).trim() !== '') {
          if (displayValue !== 'No completado') {
            displayValue += ` ${item.unit}`;
          }
        }
        return {
          id: key,
          parameter: item.displayName,
          valueDisplay: displayValue,
        }
      });

    if (dataEntries.length === 0) {
      return;
    }

    return (
      <Table
        className='cmt-table antd-table-custom-class'
        dataSource={dataEntries}
        pagination={false}
        rowKey='id'
        size="small"
      >
        <Table.Column title="Item" dataIndex='parameter' key='parameter' width='60%' />
        <Table.Column
          title="Respuesta"
          dataIndex='valueDisplay'
          key='valueDisplay'
          width='40%'
          align='left'
          className='response-column-text'
        />
      </Table>
    );
  };
  const renderResultsSection = () => {
    const resultsArray = Array.isArray(calculatorState) ? [...calculatorState].sort((first, second) => first.order - second.order) : [];
    return (
      <Row justify="center" gutter={[0, 25]} className="pl-20">
        <Col span={24}>
          <h2>Resultados</h2>
        </Col>
        <Col span={24}>
          <Table
            className='cmt-table'
            dataSource={resultsArray}
            pagination={false}
            rowKey='id'
            size="small"
          >
            <Table.Column
              title="Procedimientos"
              dataIndex='name'
              key='name'
              width='50%'
              align="left"
            />
            <Table.Column
              title="Respuesta"
              key='result'
              width='50%'
              align="center"
              render={(text, record) => (
                <Tag className='cmt-tag'>
                  {(record.result !== undefined && record.result !== null) ? String(record.result) : 'Sin Resultado'}
                </Tag>
              )}
            />
          </Table>
        </Col>
        <Col span={24}>
          <Row justify="end">
            <Col className="p-20">
              <HasAccess
                roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'equipo_medico_externo']}
              >
                <Button
                  className="cmt-button primary"
                  type="primary"
                  onClick={handleCalculate}
                  disabled={loadingState || !calculatorData || progressState < 100}
                  loading={loadingState}
                >
                  Calcular resultados
                </Button>
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderParametersSection = () => {
    return (
      <Row justify="center" gutter={[0, 20]}>
        <Col span={24}>
          <h2>Parámetros</h2>
        </Col>
        {calculatorData && Object.keys(calculatorData).length > 0 && (
          <Col span={24}>
            <p className="progress-text mb-5">
              <strong>{progressState.toFixed(0)}% completado</strong>
            </p>
            <Progress percent={progressState} showInfo={false} />
          </Col>
        )}
        <Col span={24} style={{ marginTop: '10px' }}>
          {loadingState && !calculatorData ? <Spin className="cmt-spin" tip="Cargando datos..." /> : renderDisplayTable()}
        </Col>
        <Col span={24}>
          <Row justify="end">
            <Col className="p-20">
              <Button
                className="cmt-button green pr-20 pl-20 mb-50"
                type="primary"
                size='small'
                icon={<ArrowUpOutlined />}
                onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >
                Ir al inicio
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  if (loadingState && !calculatorData && calculatorState.length === 0) {
    return (
      <div className="text-center p-50">
        <Spin className="cmt-spin" size="large" tip="Cargando..." />
      </div>
    );
  }

  return (
    <Row gutter={from === 'consultation' ? 0 : 32} justify="center" className="p-20">
      {from === 'consultation' ? (
        <>
          <Col lg={24} xs={24} className={"col-results mb-lg-10 mb-md-10 mb-sm-10 mb-xs-10"}>
            {renderResultsSection()}
          </Col>
          <Col lg={24} xs={24} className="col-results p-20 mb-lg-10 mb-md-10 mb-sm-10 mb-xs-10">
            {renderParametersSection()}
          </Col>
        </>
      ) : (
        <>
          <Col lg={12} xs={24} className="p-20 mt-lg-0 mt-md-30 mt-sm-30 mt-xs-30 col-results">
            {renderParametersSection()}
          </Col>
          <Col lg={12} xs={24}
            className={"mt-lg-0 mt-md-30 mt-sm-30 mt-xs-30 col-results"}>
            {renderResultsSection()}
          </Col>
        </>
      )}
    </Row>
  );
};

export default CalculatorForm;
